import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, GraduationCap, BookOpen, Microscope, Mail, Phone, MapPin, Calendar, Award, Loader, Sparkles, Heart } from 'lucide-react'
import { getTutorProfile, getTutorBySlug, getAllResearchGroups } from '../services/firestore'
import { QRCodeCanvas } from 'qrcode.react'
import { capitalizeName } from '../utils/formatters'
import './TutorProfile.css'

const TutorProfile = () => {
  const { tutorId } = useParams()
  const [tutor, setTutor] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tutorResearchGroups, setTutorResearchGroups] = useState([])

  useEffect(() => {
    loadTutorProfile()
  }, [tutorId])

  const loadTutorProfile = async () => {
    try {
      setLoading(true)
      // Primero intentamos buscarlo como ID directo
      let result = await getTutorProfile(tutorId)

      // Si no se encuentra por ID, intentamos por slug
      if (!result.success) {
        result = await getTutorBySlug(tutorId)
      }

      if (result.success) {
        const tutorData = result.data
        setTutor(tutorData)
        
        // Cargar los nombres de los grupos de investigación
        if (tutorData.researchGroups && tutorData.researchGroups.length > 0) {
          const groupsResult = await getAllResearchGroups()
          if (groupsResult.success) {
            const userGroups = groupsResult.data.filter(g => tutorData.researchGroups.includes(g.id))
            setTutorResearchGroups(userGroups)
          }
        }
      } else {
        setError(result.error || 'Tutor no encontrado')
      }
    } catch (err) {
      setError('Error al cargar el perfil del tutor')
      console.error('Error loading tutor:', err)
    } finally {
      setLoading(false)
    }
  }

  const profileUrl = tutor?.slug
    ? `${window.location.origin}/tutor/${tutor.slug}`
    : `${window.location.origin}/tutor/${tutor?.id}`

  if (loading) {
    return (
      <div className="tutor-profile-page">
        <div className="container">
          <div className="loading-container">
            <Loader size={48} className="spinner" />
            <p>Cargando perfil del tutor...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !tutor) {
    return (
      <div className="tutor-profile-page">
        <div className="container">
          <div className="error-message">
            <h2>Tutor no encontrado</h2>
            <p>{error || 'El tutor que buscas no existe.'}</p>
            <Link to="/tutores" className="back-button">
              <ArrowLeft size={20} />
              Volver a Tutores
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const getTutorRoles = (t) => {
    if (!t) return []
    if (t.tutorRoles && Array.isArray(t.tutorRoles)) {
      return t.tutorRoles
    }
    if (t.tutorType === 'eucs') return ['eucs']
    if (t.tutorType === 'standard') return ['vtic']
    if (t.tutorType === 'aurora') return ['aurora']
    if (t.tutorType === 'phuma') return ['phuma']
    return ['vtic']
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    try {
      const [year, month, day] = dateStr.split('-')
      const date = new Date(year, month - 1, day)
      return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })
    } catch {
      return dateStr
    }
  }

  if (tutor.isRetired === true) {
    const start = tutor.retiredData?.startDate ? formatDate(tutor.retiredData.startDate) : 'Inicio'
    const end = tutor.retiredData?.endDate ? formatDate(tutor.retiredData.endDate) : 'Fin'
    
    return (
      <div className="tutor-profile-page retired-honor-theme">
        <div className="container">
          <div className="profile-header">
            <Link to="/mural-retiro" className="back-button-honor">
              <ArrowLeft size={20} />
              Volver al Mural de Retiro
            </Link>
            <h1 className="honor-main-title">Cuadro de Honor</h1>
          </div>

          <div className="honor-board-container">
            <div className="honor-card-glow"></div>
            <div className="honor-ribbon">Retirado</div>
            <div className="honor-profile-main">
              <div className="honor-avatar-section">
                <div className="honor-avatar-wrapper">
                  <img
                    src={tutor.photoURL || tutor.avatar || 'https://res.cloudinary.com/dcm2dsjov/image/upload/v1756745742/Agregar_un_subt%C3%ADtulo_9_dqgivn.png'}
                    alt={capitalizeName(tutor.fullName)}
                  />
                  <div className="honor-badge-icon">
                    <Award size={28} />
                  </div>
                </div>
              </div>
              <div className="honor-details-section">
                <div className="honor-badge-text">
                  <Award size={16} /> VOLUNTARIO DE HONOR
                </div>
                <h2 className="honor-tutor-name">{capitalizeName(tutor.fullName)}</h2>
                
                <div className="honor-career">
                  <GraduationCap size={20} />
                  <span>{tutor.career || 'Tutor de VTIC'} {tutor.year && `- ${tutor.year}`}</span>
                </div>

                <div className="volunteering-dates-large">
                  <Calendar size={18} />
                  <span>Periodo de Voluntariado: <strong>{start}</strong> al <strong>{end}</strong></span>
                </div>

                {/* Historial de Roles */}
                <div className="honor-past-roles">
                  <span className="past-roles-label">Roles Desempeñados:</span>
                  <div className="honor-roles-badges" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
                    {getTutorRoles(tutor).map(role => {
                      const config = {
                        vtic: { label: 'Lucem Aspicio', bg: '#11163e', color: '#ffffff' },
                        aurora: { label: 'Aurora', bg: '#D10056', color: '#ffffff' },
                        phuma: { label: 'Phuma', bg: '#B35C00', color: '#ffffff' },
                        eucs: { label: 'EUCS', bg: '#7E161C', color: '#ffffff' }
                      }[role] || { label: role, bg: '#6b7280', color: '#ffffff' }
                      return (
                        <span key={role} style={{
                          backgroundColor: config.bg,
                          color: config.color,
                          padding: '4px 12px',
                          borderRadius: '15px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          display: 'inline-flex',
                          alignItems: 'center'
                        }}>
                          {config.label}
                        </span>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="honor-tribute-section">
              <h3>
                <Sparkles size={20} />
                Biografía y Reconocimiento de Retiro
              </h3>
              <div className="tribute-quote">
                <p className="tribute-text">
                  {tutor.retiredData?.honorBio || 'Agradecemos profundamente el valioso tiempo, dedicación y conocimiento brindado a nuestra comunidad durante su periodo de servicio voluntario. Su labor ha sido fundamental en la formación académica y desarrollo de nuestros estudiantes.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const roles = getTutorRoles(tutor)
  const isEUCS = roles.includes('eucs')

  return (
    <div className={`tutor-profile-page ${isEUCS ? 'tutor-eucs-theme' : 'tutor-vtic-theme'}`}>
      <div className="container">
        {/* Header con botón de regreso */}
        <div className="profile-header">
          <Link to="/tutores" className="back-button">
            <ArrowLeft size={20} />
            Volver a Tutores
          </Link>
          <h1>Perfil del Tutor</h1>
        </div>

        {/* Información principal del tutor */}
        <div className="tutor-main-info">
          <div className="tutor-avatar" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            <img
              src={tutor.photoURL || tutor.avatar || 'https://res.cloudinary.com/dcm2dsjov/image/upload/v1756745742/Agregar_un_subt%C3%ADtulo_9_dqgivn.png'}
              alt={`${capitalizeName(tutor.fullName) || 'Tutor'} Avatar`}
            />
            {tutor.isAccredited && (
              <span style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                color: '#38bdf8',
                padding: '4px 12px',
                borderRadius: '15px',
                fontSize: '11px',
                fontWeight: '700',
                boxShadow: '0 2px 6px rgba(56, 189, 248, 0.2)',
                border: '1px solid #0284c7',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap'
              }}>
                <Microscope size={12} /> Acreditado
              </span>
            )}
          </div>
          <div className="tutor-details">
            <h2 className="tutor-name">{capitalizeName(tutor.fullName) || 'Sin nombre'}</h2>

            {/* Etiquetas de roles del tutor */}
            {(() => {
              const getTutorRoles = (t) => {
                if (!t) return []
                if (t.tutorRoles && Array.isArray(t.tutorRoles)) {
                  return t.tutorRoles
                }
                if (t.tutorType === 'eucs') return ['eucs']
                if (t.tutorType === 'standard') return ['vtic']
                if (t.tutorType === 'aurora') return ['aurora']
                if (t.tutorType === 'phuma') return ['phuma']
                return ['vtic']
              }
              return (
                <div className="tutor-roles-badges" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px', marginTop: '4px' }}>
                  {getTutorRoles(tutor).map(role => {
                    const config = {
                      vtic: { label: 'Lucem Aspicio', bg: '#11163e', color: '#ffffff' },
                      aurora: { label: 'Aurora', bg: '#D10056', color: '#ffffff' },
                      phuma: { label: 'Phuma', bg: '#B35C00', color: '#ffffff' },
                      eucs: { label: 'EUCS', bg: '#7E161C', color: '#ffffff' }
                    }[role] || { label: role, bg: '#6b7280', color: '#ffffff' }
                    return (
                      <span key={role} style={{
                        backgroundColor: config.bg,
                        color: config.color,
                        padding: '4px 12px',
                        borderRadius: '15px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        display: 'inline-flex',
                        alignItems: 'center'
                      }}>
                        {config.label}
                      </span>
                    )
                  })}
                </div>
              )
            })()}
            
            {tutorResearchGroups.length > 0 && (
              <div className="investigador-badge-container" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                <span className="investigador-badge" style={{ backgroundColor: '#1A2B5B', color: 'white', padding: '4px 10px', borderRadius: '15px', fontSize: '13px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Microscope size={14} /> Investigador
                </span>
                {tutorResearchGroups.map(g => (
                  <span key={g.id} className="group-abbr-badge" style={{ backgroundColor: '#f0fdfa', border: '1px solid #ccfbf1', color: '#0d9488', padding: '4px 10px', borderRadius: '15px', fontSize: '13px', fontWeight: 'bold' }}>
                    {g.abbr}
                  </span>
                ))}
              </div>
            )}
            
            <div className="tutor-career">
              <GraduationCap size={20} />
              <span>{tutor.career || 'Sin carrera'} {tutor.year && `- ${tutor.year}`}</span>
            </div>
            <p className="tutor-bio">{tutor.bio || 'Sin biografía disponible'}</p>
            <div className="tutor-contact">
              {tutor.email && (
                <div className="contact-item">
                  <Mail size={16} />
                  <span>{tutor.email}</span>
                </div>
              )}
              {tutor.phone && (
                <div className="contact-item">
                  <Phone size={16} />
                  <span>{tutor.phone}</span>
                </div>
              )}
              {tutor.location && (
                <div className="contact-item">
                  <MapPin size={16} />
                  <span>{tutor.location}</span>
                </div>
              )}
            </div>
          </div>

          <div className="tutor-qr-section">
            <div className="qr-container">
              <QRCodeCanvas
                value={profileUrl}
                size={120}
                level="H"
                includeMargin={true}
                className="qr-code"
              />
              <p>Escanea para ver mi perfil</p>
            </div>
          </div>
        </div>

        {/* Investigaciones */}
        {tutor.research && tutor.research.length > 0 && (
          <div className="profile-section">
            <h3>
              <Microscope size={20} />
              Investigaciones
            </h3>
            <div className="research-list">
              {tutor.research.map((research, index) => (
                <div key={index} className="research-item">
                  <div className="research-header">
                    <h4>{research.title || research}</h4>
                    {research.status && (
                      <span className={`status ${research.status.toLowerCase().replace(' ', '-')}`}>
                        {research.status}
                      </span>
                    )}
                  </div>
                  {research.description && (
                    <p>{research.description}</p>
                  )}
                  {research.year && (
                    <div className="research-year">
                      <Calendar size={14} />
                      <span>{research.year}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Habilidades */}
        {tutor.skills && tutor.skills.length > 0 && (
          <div className="profile-section">
            <h3>Habilidades</h3>
            <div className="skills-grid">
              {tutor.skills.map((skill, index) => (
                <div key={index} className="skill-tag">
                  {skill}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Logros y reconocimientos */}
        {tutor.achievements && tutor.achievements.length > 0 && (
          <div className="profile-section">
            <h3>
              <Award size={20} />
              Logros y Reconocimientos
            </h3>
            <div className="achievements-list">
              {tutor.achievements.map((achievement, index) => (
                <div key={index} className="achievement-item">
                  <Award size={16} />
                  <span>{achievement}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TutorProfile
